import { MenuItem, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useRef, useState } from 'react';
import {
    useApolloClient,
    useMutation,
    useQuery,
    skipToken,
} from '@apollo/client/react';
import { Navigate, useLocation } from 'react-router';
import { mdiClose, mdiPencil } from '@mdi/js';

import { DrawerActionDropdown, PageDrawer } from 'common/components/PageDrawer';
import { IPageDrawerProps } from 'common/components/PageDrawer/PageDrawer';
import {
    DeleteOfferEventDocument,
    DeleteOfferDocument,
    OfferEventDocument,
    IOfferEventType,
    IContentTypeValue,
    CopyOfferEventDocument,
    CopyOfferDocument,
} from 'graphql/types';
import { IGlobalDrawerType } from 'common/types';
import { ApolloError } from 'common/components/ApolloError';
import { useSnackbar } from 'common/hooks/useSnackbar';
import { Loader } from 'common/components/Loader';
import { isUUID } from 'common/utils/isUUID';
import { getUrl } from 'route/utils/getUrl';
import { useRouteMatch } from 'route/hooks/useRouteMatch';
import { PageTitle } from 'common/components/PageTitle';
import { useApolloError } from 'common/hooks/useApolloError';
import { BackgroundImg } from 'common/components/Img';
import { AuthorButton } from 'user/components/AuthorButton';
import { OfferPermissionsContext } from 'offer/contexts';
import { OfferEventPermissionsContext } from 'offer/contexts/OfferPermissionsContext';
import { OfferEventDetail } from 'offer/components/OfferEventDetail';
import { useHashMatch } from 'route/hooks/useHashMatch';
import { ConfirmDialog } from 'common/components/ConfirmDialog';
import { useRouteQuery } from 'route/hooks/useRouteQuery';
import { CancelOfferEventButton } from 'offer/components/CancelOfferEventButton';
import { useGlobalDrawer } from 'common/hooks/useGlobalDrawer';
import { Button } from 'common/components/Button';
import { Icon } from 'common/components/Icon';
import { ROUTES } from 'route/constants/routes';

interface IProps extends IPageDrawerProps {
    includeOfferFields?: boolean;
    isOfferDetailDrawer?: boolean;
    loading?: boolean;
    offerEventId?: string;
    offerLoading?: boolean;
    onClose?: () => void;
}

export const OfferEventDetailDrawer = ({
    includeOfferFields,
    isOfferDetailDrawer,
    loading,
    offerEventId,
    offerLoading,
    onClose,
    ...other
}: IProps) => {
    const [translate] = useTranslation();
    const location = useLocation();
    const { openGlobalDrawer } = useGlobalDrawer();
    const routeQuery = useRouteQuery();
    const [displaySnackbar] = useSnackbar();
    const isOfferEventDetailDrawerPage = useHashMatch(
        'OFFER_EVENT_DETAIL_DRAWER'
    );
    const client = useApolloClient();

    const offerEventIdParam = isOfferEventDetailDrawerPage
        ? routeQuery.get('id')
        : undefined;

    const drawerRef = useRef<HTMLDivElement | null>(null);

    const eventId = offerEventId || offerEventIdParam || '';
    const isEventDetailPage = !!useRouteMatch('OFFER_EVENT_DETAIL_DRAWER');

    const {
        data,
        loading: offerEventLoading,
        error,
    } = useQuery(
        OfferEventDocument,
        eventId && !loading
            ? {
                  variables: { id: eventId },
                  notifyOnNetworkStatusChange: true,
              }
            : skipToken
    );

    const [copyOfferEvent, { loading: copyOfferEventLoading }] = useMutation(
        CopyOfferEventDocument
    );
    const [deleteOfferEvent, { loading: deleteOfferEventLoading }] =
        useMutation(DeleteOfferEventDocument);

    const [copyOffer, { loading: copyOfferLoading }] =
        useMutation(CopyOfferDocument);
    const [deleteOffer, { loading: deleteOfferLoading }] =
        useMutation(DeleteOfferDocument);

    const [showCopyDialog, setShowCopyDialog] = useState(false);

    const [edit, setEdit] = useState(false);
    const { showApolloError } = useApolloError();

    const { offerEvent } = data || {};

    const { offer } = offerEvent || {};
    const offerPermissions = offer?.permissions
        ? JSON.parse(offer?.permissions)
        : undefined;

    const { canUpdate = false } = offerPermissions || {};

    const offerEventPermissions = offerEvent?.permissions
        ? JSON.parse(offerEvent?.permissions)
        : undefined;

    if (
        isEventDetailPage &&
        (!isUUID(eventId) || (!offerEventLoading && !offerEvent))
    ) {
        return <Navigate to={getUrl('PAGE_NOT_FOUND')} />;
    }

    const handleClose = () => {
        if (edit) {
            setEdit(false);

            return;
        }

        onClose?.();

        setEdit(false);
    };

    const clearCacheForOfferLists = () => {
        client.cache.evict({ fieldName: 'offerEvents' });
        client.cache.evict({ fieldName: 'offers' });
        client.cache.evict({ fieldName: 'paginatedOfferEvents' });
        client.cache.gc();
    };

    const handleCopyOfferEvent = async () => {
        try {
            if (isOfferDetailDrawer) {
                if (!offer?.id) return;

                const response = await copyOffer({
                    variables: { id: offer.id },
                });

                const newOffer = response.data?.copyOffer?.offer;

                if (newOffer) {
                    openGlobalDrawer(
                        {
                            type: IGlobalDrawerType.Offer,
                            itemId: newOffer.id,
                        },
                        location
                    );
                }
            } else {
                const response = await copyOfferEvent({
                    variables: { id: eventId },
                });
                const newOfferEvent = response.data?.copyOfferEvent?.offerEvent;

                if (newOfferEvent) {
                    openGlobalDrawer(
                        {
                            type: IGlobalDrawerType.OfferEvent,
                            itemId: newOfferEvent.id,
                        },
                        location
                    );
                }
            }
        } catch (error) {
            showApolloError(error);

            return;
        }

        clearCacheForOfferLists();

        setShowCopyDialog(false);

        displaySnackbar(translate('offerActionSuccess.copy'), {
            variant: 'success',
        });
    };

    const handleDeleteOfferEvent = async () => {
        if (isOfferDetailDrawer) {
            if (!offer?.id) return;

            try {
                await deleteOffer({
                    variables: { id: offer.id },
                    update: (cache) => {
                        // Remove offer from cache
                        cache.evict({ id: `Offer:${offer.id}` });
                        cache.gc();
                    },
                });

                displaySnackbar(translate('offerActionSuccess.delete'), {
                    variant: 'success',
                });
            } catch (error) {
                showApolloError(error);
            }
        } else {
            if (!eventId) return;

            try {
                await deleteOfferEvent({
                    variables: { id: eventId },
                    update: (cache) => {
                        // Remove item from cache
                        cache.evict({ id: `OfferEvent:${eventId}` });
                        cache.gc();
                    },
                });

                displaySnackbar(translate('offerEventActionSuccess.delete'), {
                    variant: 'success',
                });
            } catch (error) {
                showApolloError(error);
            }
        }

        clearCacheForOfferLists();

        setEdit(false);

        onClose?.();
    };

    const handleEdit = () => {
        // Timeout to avoid translation being displayed while menu is still closing
        setTimeout(() => {
            setEdit(!edit);
        }, 100);
    };

    const isLoading = loading || offerEventLoading || offerLoading;
    const isElearning = offerEvent?.type === IOfferEventType.Elearning;
    const isMeeting =
        offerEvent?.type === IOfferEventType.Meeting ||
        offerEvent?.type === IOfferEventType.Webinar;
    const headerImage = !edit && offerEvent?.image?.url;
    const showCancelEventButton =
        !loading && !!offerEvent && canUpdate && isMeeting;
    const showAuthorButton = !!(
        !isLoading &&
        canUpdate &&
        !!offer?.id &&
        isOfferDetailDrawer &&
        !isElearning
    );

    // Hide edit button on all pages other than pages part of the offers overview page
    // This solution is taking into account the direct access of the drawer url
    const hideEditButton = !location.pathname.startsWith(ROUTES.OFFER_LIST);

    const drawerActions = (
        <Stack direction="row" spacing={1}>
            {showCancelEventButton && (
                <CancelOfferEventButton offerEvent={offerEvent} />
            )}

            {showAuthorButton && (
                <AuthorButton
                    contentType={IContentTypeValue.Offer}
                    objectId={offer.id}
                />
            )}

            {!isLoading && canUpdate && !hideEditButton ? (
                !edit ? (
                    !isElearning ? (
                        <DrawerActionDropdown>
                            <MenuItem onClick={handleEdit}>
                                {translate('edit')}
                            </MenuItem>

                            <MenuItem onClick={() => setShowCopyDialog(true)}>
                                {translate('copy')}
                            </MenuItem>
                        </DrawerActionDropdown>
                    ) : (
                        <Button
                            size="small"
                            startIcon={<Icon path={mdiPencil} size="1.9rem" />}
                            variant="outlined"
                            onClick={handleEdit}
                        >
                            {translate('edit')}
                        </Button>
                    )
                ) : (
                    <Button
                        size="small"
                        startIcon={<Icon path={mdiClose} size="1.9rem" />}
                        variant="outlined"
                        onClick={handleEdit}
                    >
                        {translate('cancelEdit')}
                    </Button>
                )
            ) : undefined}
        </Stack>
    );

    return (
        <OfferPermissionsContext.Provider value={offerPermissions}>
            <OfferEventPermissionsContext.Provider
                value={offerEventPermissions}
            >
                <PageDrawer
                    actions={drawerActions}
                    closeOnBackdropClick={!edit}
                    disableClose={deleteOfferLoading || deleteOfferEventLoading}
                    headerProps={{ floating: !!headerImage }}
                    ref={drawerRef}
                    onClose={handleClose}
                    {...other}
                >
                    {error && <ApolloError error={error} />}

                    <PageTitle>
                        {`${translate('offers')} - ${translate(
                            `offer.${edit ? 'updateEvent' : 'event'}`
                        )}`}
                    </PageTitle>

                    {isLoading && <Loader />}

                    <ConfirmDialog
                        confirmText={translate('copy')}
                        loading={copyOfferEventLoading || copyOfferLoading}
                        open={showCopyDialog}
                        title={translate(
                            isOfferDetailDrawer
                                ? 'copyOfferDialog.title'
                                : 'copyOfferEventDialog.title'
                        )}
                        onCancel={() => {
                            setShowCopyDialog(false);
                        }}
                        onClose={() => setShowCopyDialog(false)}
                        onConfirm={handleCopyOfferEvent}
                    >
                        {translate(
                            isOfferDetailDrawer
                                ? 'copyOfferDialog.text'
                                : 'copyOfferEventDialog.text'
                        )}
                    </ConfirmDialog>

                    {offerEvent && !isLoading && (
                        <>
                            {headerImage && (
                                <BackgroundImg
                                    src={headerImage}
                                    sx={{ minHeight: '250px' }}
                                />
                            )}

                            <OfferEventDetail
                                drawerRef={drawerRef}
                                edit={edit}
                                includeOfferFields={includeOfferFields}
                                isOfferDetailDrawer={isOfferDetailDrawer}
                                offerEvent={offerEvent}
                                onCancelEdit={() => {
                                    setEdit(false);
                                }}
                                onClose={handleClose}
                                onDelete={handleDeleteOfferEvent}
                            />
                        </>
                    )}
                </PageDrawer>
            </OfferEventPermissionsContext.Provider>
        </OfferPermissionsContext.Provider>
    );
};
